import { Box, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core'
import { updateLargePositions } from 'js/actions/market'
import { AmountDenomCell, CellLink, DataLoadSegment, ListTable, PaginationByPage, Section, TableEmptyState } from 'js/components'
import { TaskNames } from 'js/constants'
import { useTaskSubscriber } from 'js/hooks'
import React, { ReactElement, ReactNode } from 'react'
import { RestResponse, RestTypes } from 'tradehub-api-js'
import { bnOrZero, SimpleMap } from 'js/utils'

interface Props {
  largePositions?: RestTypes.ResultsPaged<RestResponse.Position>
  markets?: SimpleMap<RestResponse.Market>
}

const ActiveTasks = [
  TaskNames.App.Markets,
  TaskNames.Market.LargePositions,
  TaskNames.Market.LargeFilter,
]

const LargePositions: React.FunctionComponent<Props> = ({
  largePositions,
  markets,
}: Props): ReactElement<Props> => {
  const [page, setPage] = React.useState(1)
  const [loading] = useTaskSubscriber(...ActiveTasks)

  return (
    <Section title="Largest Positions">
      <DataLoadSegment loading={loading}>
        <ListTable>
          <TableHead>
            <TableRow>
              <TableCell>Account</TableCell>
              <TableCell align="right">Position Size</TableCell>
              <TableCell align="right">Avg Price</TableCell>
              <TableCell align="right">Realized PnL</TableCell>
              <TableCell align="right">Margin Allocated</TableCell>
              <TableCell align="right">Created At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{largePositions?.data.map(renderLargePosition)}</TableBody>
        </ListTable>

        {!!largePositions?.data?.length && (
          <Box marginTop={3}>
            <PaginationByPage
              action={updateLargePositions}
              setPage={setPage}
              page={page}
              response={largePositions}
            />
          </Box>
        )}
        {(!largePositions?.data?.length) && (
          <TableEmptyState itemName="positions" />
        )}
      </DataLoadSegment>
    </Section>
  )

  function renderLargePosition(
    position: RestResponse.Position,
    index: number,
  ): ReactNode {
    const entryPriceBN = bnOrZero(position.entry_price)
    const realizedPnlBN = bnOrZero(position.realized_pnl)
    const allocatedMarginBN = bnOrZero(position.allocated_margin_amount)
    const lotDp: number = bnOrZero(markets?.[position.market]?.lot_size).dp()
    const lotsBN = bnOrZero(position.lots)
    const denom = position.market.split('_')[0].toUpperCase()

    const { username, address } = position
    return (
      <TableRow key={address + index} hover>
        <TableCell>
          <CellLink to={`/account/${address}`}>
            {username === '' ? address : username}
          </CellLink>
        </TableCell>
        <AmountDenomCell amount={lotsBN.toFormat(lotDp)} denom={denom} />
        <TableCell align="right">{entryPriceBN.toFormat(2)}</TableCell>
        <TableCell align="right">{realizedPnlBN.toFormat(2)}</TableCell>
        <AmountDenomCell denom={position.allocated_margin_denom} amount={allocatedMarginBN.toFormat(2)} />
        <TableCell align="right">
          <CellLink to={`/block/${position.created_block_height}`}>
            {position.created_block_height}
          </CellLink>
        </TableCell>
      </TableRow>
    )
  }
}

export default LargePositions
